// Setup steps for each MCP client in the agent selector. Verified against
// each client's official documentation on 2026-10-06 (links in `docs`).
// Keep these exact: people paste them.

export type Step = { text: string; code?: string; codeLabel?: string };

export type Client = {
  id: string;
  name: string;
  docs: string;
  steps: (url: string) => Step[];
  /**
   * The hero's copy-and-paste prompt: tells this agent how to connect the
   * MCP server, or how to walk the user through it when it can't. Built from
   * the same verified steps.
   */
  onboard: (url: string) => string;
  oneClick?: { label: string; href: (url: string) => string };
  note?: string;
  /** Chat apps where the agent can't add a server itself and must guide the user. */
  guided?: boolean;
  /** Extra guidance for an agent following /agent-setup/prompt.txt. */
  agentNote?: string;
};

const json = (value: unknown) => JSON.stringify(value, null, 2);

/**
 * The homepage's one-line prompt, modelled on Cloudflare's agent setup
 * prompt. The agent fetches /agent-setup/prompt.txt (src/worker/agentSetup.ts).
 */
export const setupPrompt = (origin: string, partner?: string | null) =>
  `Fetch and execute the appropriate instructions to set me up for Moonlauncher from ${origin}/${partner ? `p/${partner}/prompt.txt` : "agent-setup/prompt.txt"}`;

const GOAL = "so you can launch pump.fun tokens for me";
const NEXT =
  "After that, when I ask you to launch a token, call Moonlauncher's start_launch tool first and ask me its questions.";
/** Why it's reasonable to let the agent call these tools without asking. */
export const SAFE =
  "Moonlauncher's tools only prepare launches and fee claims and read their status. Nothing is created, paid or sent until I sign in my own wallet.";
/** The tool names Claude Code gives a server named "moonlauncher". */
export const CLAUDE_CODE_TOOLS = ["start_launch", "launch_token", "launch_status", "token_info", "creator_fees"].map(
  (tool) => `mcp__moonlauncher__${tool}`,
);

const numbered = (steps: string[]) => steps.map((step, i) => `${i + 1}. ${step}`).join("\n\n");

/** For agents that can run the command or edit the file themselves. */
const selfSetup = (setup: string, allow: string, restart: string) =>
  `Set up the Moonlauncher MCP server ${GOAL}.\n\n${numbered([setup, allow])}\n\nThen tell me to ${restart} so the tools load. ${NEXT}`;

/** For clients where approval settings are unverified: explain, don't edit. */
const allowGeneric = (app: string) =>
  `If ${app} will ask me to approve each Moonlauncher tool call, tell me how to always allow this server's tools, and why that's safe. ${SAFE}`;

/** For chat apps that can't add a server themselves. */
const guided = (app: string, steps: string[]) =>
  `Help me add the Moonlauncher MCP server to ${app} ${GOAL}. You can't add it yourself, so walk me through these steps one at a time:\n${steps
    .map((step, i) => `${i + 1}. ${step}`)
    .join("\n")}\n${steps.length + 1}. When ${app} first asks to use a Moonlauncher tool, I can allow it. ${SAFE}\n\n${NEXT}`;

export const CLIENTS: Client[] = [
  {
    id: "claude-code",
    name: "Claude Code",
    docs: "https://code.claude.com/docs/en/mcp",
    steps: (url) => [
      {
        text: "Run this in your terminal:",
        code: `claude mcp add --transport http --scope user moonlauncher ${url}`,
        codeLabel: "Claude Code command",
      },
      { text: "Start Claude Code and ask it to launch a token." },
      {
        text: "Optional: so Claude Code doesn't ask about (or, in auto mode, block) each Moonlauncher call, allow its tools with /permissions or in ~/.claude/settings.json:",
        code: json({ permissions: { allow: CLAUDE_CODE_TOOLS } }),
        codeLabel: "Claude Code permission rules",
      },
    ],
    onboard: (url) =>
      selfSetup(
        `Run this command:\nclaude mcp add --transport http --scope user moonlauncher ${url}`,
        `Ask me with AskUserQuestion whether Claude Code should use Moonlauncher's tools without asking each time, and tell me why it's safe. ${SAFE} If I say yes, add these rules to "permissions" > "allow" in ~/.claude/settings.json, keeping everything already there. Claude Code will ask me to approve the edit; if you can't make it, tell me to add them with /permissions.\n${CLAUDE_CODE_TOOLS.join("\n")}`,
        "restart Claude Code",
      ),
    note: "--scope user makes Moonlauncher available in every project. Leave it out to add it to the current project only.",
  },
  {
    id: "claude",
    name: "Claude",
    docs: "https://support.claude.com/en/articles/11175166-getting-started-with-custom-connectors-using-remote-mcp",
    steps: (url) => [
      { text: "In Claude (web or desktop), open Customize, then Connectors. Choose + Add, then Add custom connector." },
      { text: "Name it moonlauncher and paste this URL, then choose Continue:", code: url, codeLabel: "MCP server URL" },
      { text: "For authentication, choose No sign in, then Add." },
      { text: "In a chat, turn it on from + then Connectors." },
    ],
    onboard: (url) =>
      guided("Claude", [
        "Open Customize, then Connectors. Choose + Add, then Add custom connector.",
        `Name it moonlauncher and paste this URL, then choose Continue: ${url}`,
        "For authentication, choose No sign in, then Add.",
        "In a chat, turn it on from + then Connectors.",
      ]),
    note: "On Team and Enterprise plans, an owner adds the connector first in Organization settings, then Connectors. The Free plan allows one custom connector.",
    guided: true,
  },
  {
    id: "cursor",
    name: "Cursor",
    docs: "https://cursor.com/docs/mcp",
    oneClick: {
      label: "Add to Cursor",
      href: (url) =>
        `https://cursor.com/install-mcp?name=moonlauncher&config=${encodeURIComponent(btoa(JSON.stringify({ url })))}`,
    },
    steps: (url) => [
      {
        text: "Add this to ~/.cursor/mcp.json for every project, or .cursor/mcp.json for one project:",
        code: json({ mcpServers: { moonlauncher: { url } } }),
        codeLabel: "Cursor configuration",
      },
    ],
    onboard: (url) =>
      selfSetup(
        `Add this server to ~/.cursor/mcp.json. Create the file if it doesn't exist, and keep any servers already there:\n${JSON.stringify({ mcpServers: { moonlauncher: { url } } })}`,
        allowGeneric("Cursor"),
        "restart Cursor",
      ),
  },
  {
    id: "vscode",
    name: "VS Code",
    docs: "https://code.visualstudio.com/docs/agent-customization/mcp-servers",
    oneClick: {
      label: "Add to VS Code",
      href: (url) => `vscode:mcp/install?${encodeURIComponent(JSON.stringify({ name: "moonlauncher", type: "http", url }))}`,
    },
    steps: (url) => [
      { text: "Open the Command Palette and run MCP: Open User Configuration." },
      {
        text: "Add the moonlauncher server:",
        code: json({ servers: { moonlauncher: { type: "http", url } } }),
        codeLabel: "VS Code configuration",
      },
      { text: "Use it from Copilot Chat in agent mode." },
    ],
    onboard: (url) =>
      selfSetup(
        `Add this server to .vscode/mcp.json in this workspace. Create the file if it doesn't exist, and keep any servers already there:\n${JSON.stringify({ servers: { moonlauncher: { type: "http", url } } })}`,
        allowGeneric("VS Code"),
        "start the server from that file in VS Code",
      ),
    note: "Needs GitHub Copilot Chat. Your organization can restrict MCP servers.",
    agentNote:
      "Add it to the user MCP configuration (the file MCP: Open User Configuration opens). If you can't reach that file, add the same entry to .vscode/mcp.json in the current workspace.",
  },
  {
    id: "windsurf",
    name: "Windsurf",
    docs: "https://docs.devin.ai/desktop/cascade/mcp",
    steps: (url) => [
      { text: "In the Cascade panel, open the ⋯ menu and choose Open MCP config file." },
      {
        text: "Add the moonlauncher server:",
        code: json({ mcpServers: { moonlauncher: { url } } }),
        codeLabel: "Windsurf configuration",
      },
    ],
    onboard: (url) =>
      selfSetup(
        `Add this server to my MCP config file: ~/.config/devin/mcp_config.json on macOS and Linux, or %APPDATA%\\devin\\mcp_config.json on Windows. Keep any servers already there:\n${JSON.stringify({ mcpServers: { moonlauncher: { url } } })}`,
        allowGeneric("Windsurf"),
        "refresh the MCP servers in Cascade",
      ),
    note: "Windsurf is now Devin Desktop. The file is ~/.config/devin/mcp_config.json on macOS and Linux, or %APPDATA%\\devin\\mcp_config.json on Windows.",
    agentNote:
      "Edit whichever config file exists: ~/.config/devin/mcp_config.json (Devin Desktop, macOS and Linux), %APPDATA%\\devin\\mcp_config.json (Windows) or ~/.codeium/windsurf/mcp_config.json (older Windsurf). Remote servers take url or serverUrl.",
  },
  {
    id: "chatgpt",
    name: "ChatGPT",
    docs: "https://developers.openai.com/api/docs/guides/custom-mcp-server",
    steps: (url) => [
      { text: "Go to chatgpt.com/plugins, choose +, then Add custom MCP server." },
      { text: "Name it moonlauncher and paste this server URL:", code: url, codeLabel: "MCP server URL" },
      { text: "For authentication, choose No authentication. Confirm, then choose Create as a plugin." },
      { text: "Install it from your plugins, then use it with @ in the prompt box." },
    ],
    onboard: (url) =>
      guided("ChatGPT", [
        "Go to chatgpt.com/plugins, choose +, then Add custom MCP server.",
        `Name it moonlauncher and paste this server URL: ${url}`,
        "For authentication, choose No authentication. Confirm, then choose Create as a plugin.",
        "Install it from your plugins, then use it with @ in the prompt box.",
      ]),
    note: "Availability depends on your ChatGPT plan and workspace settings.",
    guided: true,
  },
  {
    id: "codex",
    name: "Codex",
    docs: "https://learn.chatgpt.com/docs/extend/mcp",
    steps: (url) => [
      { text: "Run this in your terminal:", code: `codex mcp add moonlauncher --url ${url}`, codeLabel: "Codex command" },
      {
        text: "Or add this to ~/.codex/config.toml:",
        code: `[mcp_servers.moonlauncher]\nurl = "${url}"`,
        codeLabel: "Codex configuration",
      },
    ],
    onboard: (url) =>
      selfSetup(
        `Run this command:\ncodex mcp add moonlauncher --url ${url}\nIf that doesn't work, add this to ~/.codex/config.toml instead:\n[mcp_servers.moonlauncher]\nurl = "${url}"`,
        allowGeneric("Codex"),
        "restart Codex",
      ),
    note: "The ChatGPT desktop app and the Codex IDE extension share this configuration.",
  },
  {
    id: "gemini",
    name: "Gemini CLI",
    docs: "https://geminicli.com/docs/tools/mcp-server/",
    steps: (url) => [
      {
        text: "Run this in your terminal:",
        code: `gemini mcp add --transport http --scope user moonlauncher ${url}`,
        codeLabel: "Gemini CLI command",
      },
      {
        text: "Or add this to ~/.gemini/settings.json:",
        code: json({ mcpServers: { moonlauncher: { httpUrl: url } } }),
        codeLabel: "Gemini CLI configuration",
      },
    ],
    onboard: (url) =>
      selfSetup(
        `Run this command:\ngemini mcp add --transport http --scope user moonlauncher ${url}\nIf you edit ~/.gemini/settings.json instead, use "httpUrl", not "url".`,
        `Ask me whether Gemini CLI should run Moonlauncher's tools without asking each time, and tell me why it's safe. ${SAFE} If I say yes, add "trust": true to the moonlauncher entry under "mcpServers" in ~/.gemini/settings.json.`,
        "restart Gemini CLI",
      ),
    note: 'Use httpUrl, not url: in Gemini CLI, url means the older SSE transport. To skip a confirmation on every call, add "trust": true to the moonlauncher entry.',
  },
  {
    id: "opencode",
    name: "opencode",
    docs: "https://opencode.ai/docs/mcp-servers/",
    steps: (url) => [
      {
        text: "Add this to ~/.config/opencode/opencode.json, or opencode.json in your project:",
        code: json({ $schema: "https://opencode.ai/config.json", mcp: { moonlauncher: { type: "remote", url, enabled: true } } }),
        codeLabel: "opencode configuration",
      },
    ],
    onboard: (url) =>
      selfSetup(
        `Add this server to ~/.config/opencode/opencode.json, inside "mcp". Keep everything already there:\n"moonlauncher": ${JSON.stringify({ type: "remote", url, enabled: true })}`,
        allowGeneric("opencode"),
        "restart opencode",
      ),
  },
];
